import type { CSSProperties } from 'react';
import type { NavItem } from './routeMap';

// Pure state helpers for the page host (keep-alive-page-host AC1/AC2/AC5/AC6).
// The host keeps visited keep-alive pages mounted and toggles their visibility
// rather than unmounting them. These functions own the *decisions* so they can
// be unit-tested without a DOM; PageHost wires them to React.

// A route opts out of keep-alive by declaring `remountOnReturn: true` in the
// route map (AC5). Default — when the flag is absent, or the key is unknown —
// is keep-alive. The opt-out lives on the route, never inside the page.
export function isKeptKey(item: NavItem | undefined): boolean {
  return item?.remountOnReturn !== true;
}

// One mounted slot: its nav key and whether it was mounted as keep-alive. The
// host keeps the kept flag with each slot so a previously-visited non-kept key
// can be dropped on the next transition (AC5) without re-deriving it.
export interface MountedSlot {
  key: string;
  kept: boolean;
}

// Given the slots currently mounted, the key becoming active, and whether that
// active key is keep-alive, return the next mounted list (first-visit insertion
// order preserved).
//
//  - A keep-alive key is added on first visit and never removed — returning to
//    it finds it still mounted (no remount, local state survives) (AC1/AC2).
//  - A remount-on-return (non-kept) key is mounted only while it is active. The
//    moment another key becomes active, the non-kept key is dropped, so leaving
//    and returning to it is a fresh mount that re-fetches from empty (AC5).
export function nextSlots(
  prev: MountedSlot[],
  active: string,
  activeKept: boolean,
): MountedSlot[] {
  // Keep every prior slot that is either keep-alive or the one still active;
  // drop a non-kept slot the instant it is no longer the active key.
  const sticky = prev.filter((s) => s.kept || s.key === active);
  const out = sticky.slice();
  const existing = out.find((s) => s.key === active);
  if (existing) {
    // Already mounted — no remount; refresh its kept flag (route flags are
    // stable, but this keeps the slot honest if a route is reconfigured).
    existing.kept = activeKept;
  } else {
    out.push({ key: active, kept: activeKept });
  }
  return out;
}

// Convenience over nextSlots for the common all-keep-alive call sites and the
// pure tests: operate on bare keys, treating each as keep-alive unless the
// active transition says otherwise. Returns the mounted key order.
export function nextMounted(prevMounted: string[], active: string, kept: boolean): string[] {
  // Reconstruct slots from the bare key list. Prior keys are keep-alive by
  // construction here (a non-kept key would already have been dropped on the
  // transition that left it), so only the active key's kept-ness can be false.
  const prevSlots: MountedSlot[] = prevMounted.map((k) => ({
    key: k,
    // The active key's prior mount carries the kept flag we are told now; any
    // other prior key is keep-alive (else it would not still be in the list).
    kept: k === active ? kept : true,
  }));
  return nextSlots(prevSlots, active, kept).map((s) => s.key);
}

// The inline style for one mounted slot: the active slot renders normally; an
// inactive slot is hidden with display:none so it stays mounted (local state
// alive) without occupying layout or showing through. display:none — not
// visibility:hidden — so Playwright `:visible` and layout both treat it as
// absent, and the active page looks byte-identical to today (AC6).
export function hostSlotStyle(key: string, active: string): CSSProperties {
  return key === active ? {} : { display: 'none' };
}
